import { useEffect, useState, useContext, useCallback } from 'react';
import { LoginContext } from "../AuthContext/LoginContext";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import TooltipTextPopupCustom from './child/TooltipTextPopupCustom';
import { Link } from "react-router-dom";

const PlantYourActivityLayer = () => {
  const [plantations, setPlantations] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  const { hasPermission } = useContext(LoginContext);

  const renderSpeciesName = (speciesName) => {
    if (Array.isArray(speciesName)) {
      return speciesName.join(', ') || 'No Data Found';
    }
    return speciesName || 'No Data Found';
  };

  /* -------------------------------------------------------------
     FETCH – memoised with useCallback
     ------------------------------------------------------------- */
  const fetchYourActivity = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/api/plantation/pafs/your-activity`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );

      if (response.data.success) {
        setPlantations(response.data.data || []);
      } else {
        toast.error("Failed to fetch your plantation activity.");
        setPlantations([]);
      }
    } catch (error) {
      console.error("Error fetching your plantation activity:", error);
      toast.error("Error fetching your plantation activity.");
      setPlantations([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  /* -------------------------------------------------------------
     INITIAL LOAD
     ------------------------------------------------------------- */
  useEffect(() => {
    fetchYourActivity();
  }, [fetchYourActivity]);   // safe – function reference is stable

  const getStatusClasses = (category) => {
    const normalized = category?.toLowerCase().replace(/\s+/g, '').replace(/-/g, '') || '';
    switch (normalized) {
      case 'public': return 'bg-success-100 text-success-700';
      case 'departmental': return 'bg-warning-100 text-warning-700';
      default: return 'bg-secondary-100 text-secondary-700';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <ToastContainer />

      {/* ------------------- LIST / SKELETON ------------------- */}
      {loading ? (
        <div className='row gy-4'>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className='col-xxl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='card h-100 radius-12 overflow-hidden'>
                <div className='card-body p-24'>
                  <Skeleton height={180} className="mb-16 radius-8" />
                  <Skeleton count={3} className="mb-8" />
                  <Skeleton width={100} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : plantations.length > 0 ? (
        <div className='row gy-4'>
          {plantations.map((plantation, i) => (
            <div key={plantation._id || i} className='col-xxl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='card h-100 radius-12 overflow-hidden shadow-sm hover-shadow-lg transition-2'>
                <div style={{ position: 'relative' }}>
                  <img
                    src={
                      plantation.images?.[0] ||
                      process.env.PUBLIC_URL + "/assets/images/blog/blog1.png"
                    }
                    alt={renderSpeciesName(plantation.speciesName)}
                    className="w-100 object-fit-cover"
                    style={{ height: "180px" }}
                    onError={(e) => {
                      e.target.src = process.env.PUBLIC_URL + "/assets/images/blog/blog1.png";
                    }}
                  />
                  <div style={{ position: 'absolute', top: '16px', left: '16px' }}>
                    <span className={`p-4 rounded-pill fw-semibold text-xs ${getStatusClasses(plantation.category)}`}>
                      {plantation.category || 'No Data Found'}
                    </span>
                  </div>
                </div>

                <div className='card-body p-24'>
                  <div className='d-flex align-items-center gap-6 justify-content-between flex-wrap mb-12'>
                    <div className='d-flex align-items-center gap-8 text-neutral-500 fw-medium'>
                      <i className='ri-calendar-2-line' />
                      {formatDate(plantation.plantationDate)}
                    </div>
                    <div className='d-flex align-items-center gap-8 text-neutral-500 fw-medium'>
                      <i className='ri-plant-line' />
                      {plantation.numberOfPlants || '0'} plants
                    </div>
                  </div>

                  <h5 className='mb-12 text-line-2 text-neutral-900'>
                    {renderSpeciesName(plantation.speciesName)}
                  </h5>

                  <div className="d-flex align-items-center gap-8 text-neutral-600 mb-12">
                    <i className="ri-map-pin-line"></i>
                    <span className="text-line-1">
                      {plantation.district}, {plantation.province}
                    </span>
                  </div>

                  <div className="d-flex flex-wrap gap-8 mb-16">
                    <span className="px-12 py-4 bg-neutral-100 rounded-pill text-neutral-600 fw-medium text-xs">
                      {plantation.plantationCampaign || 'No campaign'}
                    </span>
                    <span className="px-12 py-4 bg-neutral-100 rounded-pill text-neutral-600 fw-medium text-xs">
                      {plantation.plantationType || 'No type'}
                    </span>
                    <span className="px-12 py-4 bg-neutral-100 rounded-pill text-neutral-600 fw-medium text-xs">
                      {plantation.plantedBy || 'Unknown'}
                    </span>
                  </div>

                  {plantation.followUps?.length > 0 && (
                    <div className="bg-success-50 p-12 radius-8 mb-16">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold text-success-700">Latest Follow-up</span>
                        <span className="text-xs text-success-600">
                          {formatDate(plantation.followUps[0].updatedAt)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mt-8">
                        <span className="text-xs">Survival Rate: <strong>{plantation.followUps[0].survivalRate}%</strong></span>
                        <span className="text-xs">Avg Height: <strong>{plantation.followUps[0].averageHeight} cm</strong></span>
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center">
                    <TooltipTextPopupCustom
                      text={plantation.publicRemarks ?? 'No remarks available'}
                      truncateLength={20}
                      placement="top"
                      customClass="tooltip-info"
                    >
                      <span className="text-neutral-500 text-xs cursor-pointer">
                        {plantation.publicRemarks
                          ? plantation.publicRemarks.length > 20
                            ? `${plantation.publicRemarks.substring(0, 20)}...`
                            : plantation.publicRemarks
                          : 'No remarks'}
                      </span>
                    </TooltipTextPopupCustom>

                    {hasPermission('plantations', 'view') && (
                      <Link
                        to={`/plantation/view/${plantation._id}`}
                        className="btn btn-sm btn-success-600 d-flex align-items-center gap-1"
                      >
                        Details
                        <i className="ri-arrow-right-line d-flex"></i>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="card-body text-center py-48">
            <i className="ri-plant-line text-neutral-300 text-6xl d-block mb-16"></i>
            <h4 className="text-neutral-500 mb-8">No plantation activities found</h4>
            <p className="text-neutral-400">You haven't created any plantation activities yet.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default PlantYourActivityLayer;